import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { isNil } from 'lodash-es';

import { AddressDetailsFragment } from 'generated/graphql';

import { addressWasChangedOnNormalization, isNullOrWhitespace } from 'helpers';
import { AddressPipe } from 'shared/pipes/address.pipe';
import { isInternationalCountry } from 'core/geography';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'full-address',
  template: `
  <ng-container *ngIf="address">
    <a *ngIf="!noLink" class="link link--noline"
     [matTooltip]="originalAddress"
     [ngClass]="additionalClasses"
      [href]="mapsUrl"
      target="_blank" rel="noopener noreferrer" [title]="address | address">
      <ng-container *ngTemplateOutlet="addressLines"></ng-container>
    </a>

    <span *ngIf="noLink" [ngClass]="additionalClasses"
      [matTooltip]="originalAddress">
      <ng-container *ngTemplateOutlet="addressLines"></ng-container>
    </span>
  </ng-container>

  <!--
    Rendered via interpolation rather than [innerHTML] so that address text is always
    escaped. Tags hug the line breaks to avoid emitting stray whitespace text nodes.
  -->
  <ng-template #addressLines>
    <ng-container *ngFor="let line of displayLines; last as isLast"
      >{{ line }}<br *ngIf="!isLast"
    ></ng-container>
  </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class FullAddressComponent implements OnChanges {
  @Input() address: Partial<AddressDetailsFragment>;
  @Input() additionalClasses;
  @Input() noLink: boolean;

  addressPipe = new AddressPipe();

  /**
   * Display as single line
   */
  @Input() singleLine;

  /**
   * Display address type
   * Eg. 123 Some St, City, State 12345 ({{address_type}})
   */
  @Input() displayAddressType = false;

  /**
   * Plain text lines to render, one per visual row. Never contains markup.
   */
  displayLines: string[] = [];

  mapsUrl = '';

  ngOnChanges(): void {
    this.displayLines = this.buildDisplayLines();
    this.mapsUrl = this.buildMapsUrl();
  }

  get originalAddress(): string {
    if (!addressWasChangedOnNormalization(this.address as AddressDetailsFragment)) {
      return null;
    }

    return `This address has been autocorrected. Original address:
    ${this.addressPipe.transform(this.address.original)}`;
  }

  private buildDisplayLines(): string[] {
    if (isNil(this.address)) {
      return [];
    }

    const addressParts = this.getAddressParts(this.address as AddressDetailsFragment);
    const addressLine1 = addressParts[0].filter(this._hasValue).join(', ');
    const addressLine2 = addressParts[1].filter(this._hasValue).join(', ');

    let lines = [addressLine1, addressLine2].filter(this._hasValue);

    if (this.singleLine) {
      lines = [lines.join(', ')].filter(this._hasValue);
    }

    if (this.displayAddressType === true && lines.length > 0) {
      const lastIndex = lines.length - 1;
      lines[lastIndex] = `${lines[lastIndex]} (physical)`;
    }

    return lines;
  }

  private buildMapsUrl(): string {
    const query = this.addressPipe.transform(this.address);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
  }

  private _hasValue(v: string) {
    return !isNullOrWhitespace(v);
  }

  private getAddressParts(address: AddressDetailsFragment): string[][] {
    if (isInternationalCountry(address.country)) {
      return [[address.streetAddress], [address.city, address.zip, address.country]];
    }
    return [[address.streetAddress], [address.city, address.state, address.zip]];
  }

}
